/**
 * Profile – Conditional field visibility
 * Handles showing/hiding fields based on employment status, relationship intent,
 * and dropdown/radio "other" option toggling
 */

// Setup conditional field visibility based on employment_status
export function setupConditionalFields(): void {
  const employmentStatusGroup = document.getElementById('employment_status');
  if (!employmentStatusGroup) return;

  const updateConditionalFieldVisibility = () => {
    const checkedRadio = employmentStatusGroup.querySelector('input[type="radio"]:checked') as HTMLInputElement;
    const status = checkedRadio?.value || '';
    const studentFields = document.querySelectorAll('.student-field');
    const workFields = document.querySelectorAll('.work-field');

    const isStudent = status === 'student';
    const isEmployed = ['full_time', 'part_time', 'self_employed'].includes(status);

    studentFields.forEach(field => {
      (field as HTMLElement).style.display = isStudent ? '' : 'none';
    });

    workFields.forEach(field => {
      (field as HTMLElement).style.display = isEmployed ? '' : 'none';
    });
  };

  // Listen for change on all radio buttons in the group
  employmentStatusGroup.querySelectorAll('input[type="radio"]').forEach(radio => {
    radio.addEventListener('change', updateConditionalFieldVisibility);
  });

  // Setup relationship structure visibility based on intent
  setupRelationshipStructureVisibility();
}

// Hide relationship structure when only "friends" is selected
function setupRelationshipStructureVisibility(): void {
  const intentGroup = document.getElementById('relationship_intent');
  const structureContainer = document.getElementById('structure-field-container');
  if (!intentGroup || !structureContainer) return;

  const updateStructureVisibility = () => {
    const checkedIntents = Array.from(intentGroup.querySelectorAll('input[type="checkbox"]:checked'))
      .map(cb => (cb as HTMLInputElement).value);

    // Hide structure if no intents selected OR if only "friends" is selected
    const shouldHide = checkedIntents.length === 0 ||
      (checkedIntents.length === 1 && checkedIntents.includes('friends'));

    structureContainer.style.display = shouldHide ? 'none' : '';
  };

  // Listen for changes on all intent checkboxes
  intentGroup.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
    checkbox.addEventListener('change', updateStructureVisibility);
  });
}

// Setup dropdown + other pattern: show/hide text field when "other" is selected
export function setupDropdownWithOther(): void {
  // Handle radio group with "other" option
  const radioGroupsWithOther = document.querySelectorAll('.radio-group-with-other');
  radioGroupsWithOther.forEach(radioGroup => {
    const fieldId = radioGroup.id;
    const otherContainer = document.getElementById(`${fieldId}_other_container`);

    if (!otherContainer) return;

    radioGroup.querySelectorAll('input[type="radio"]').forEach(radio => {
      radio.addEventListener('change', () => {
        const radioEl = radio as HTMLInputElement;
        if (radioEl.checked && radioEl.value === 'other') {
          otherContainer.style.display = '';
          const otherInput = document.getElementById(`${fieldId}_other`) as HTMLInputElement;
          otherInput?.focus();
        } else if (radioEl.checked) {
          otherContainer.style.display = 'none';
        }
      });
    });
  });

  // Handle multi-select with "other" checkbox
  const otherCheckboxes = document.querySelectorAll('input[data-is-other-checkbox="true"]');
  otherCheckboxes.forEach(checkbox => {
    const checkboxEl = checkbox as HTMLInputElement;
    const parentMultiselect = checkboxEl.closest('.multiselect-with-other');
    if (!parentMultiselect) return;

    const fieldId = parentMultiselect.id;
    const otherContainer = document.getElementById(`${fieldId}_other_container`);

    if (!otherContainer) return;

    checkboxEl.addEventListener('change', () => {
      if (checkboxEl.checked) {
        otherContainer.style.display = '';
        const otherInput = document.getElementById(`${fieldId}_other`) as HTMLInputElement;
        otherInput?.focus();
      } else {
        otherContainer.style.display = 'none';
        // Clear the other text field when unchecked
        const otherInput = document.getElementById(`${fieldId}_other`) as HTMLInputElement;
        if (otherInput) {
          otherInput.value = '';
          // Trigger input event to save the cleared value
          otherInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    });
  });

  // Handle multi-select with "custom" checkbox (separate custom field, like pronouns)
  const customCheckboxes = document.querySelectorAll('input[data-is-custom-checkbox="true"]');
  customCheckboxes.forEach(checkbox => {
    const checkboxEl = checkbox as HTMLInputElement;
    const parentMultiselect = checkboxEl.closest('.multiselect-with-custom');
    if (!parentMultiselect) return;

    const fieldId = parentMultiselect.id;
    const customContainer = document.getElementById(`${fieldId}_custom_container`);

    if (!customContainer) return;

    checkboxEl.addEventListener('change', () => {
      if (checkboxEl.checked) {
        customContainer.style.display = '';
        const customInput = document.getElementById(`${fieldId}_custom`) as HTMLInputElement;
        customInput?.focus();
      } else {
        customContainer.style.display = 'none';
        // Clear the custom text field when unchecked
        const customInput = document.getElementById(`${fieldId}_custom`) as HTMLInputElement;
        if (customInput) {
          customInput.value = '';
          // Trigger input event to save the cleared value
          customInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    });
  });
}
